import type { Action } from '@reduxjs/toolkit'
import type { Observable } from 'rxjs'
import {
  catchError,
  defer,
  distinctUntilChanged,
  EMPTY,
  filter,
  fromEvent,
  map,
  merge,
  of,
  repeat,
  startWith,
  switchMap,
  timer,
  withLatestFrom
} from 'rxjs'
import type { Epic } from '../../index.js'
import {
  checkConnectivityNow,
  connectivityProbeFailed,
  connectivityProbeStarted,
  connectivityProbeSucceeded,
  reportBackendUnreachable
} from '../../Slice/Application/ApplicationSlice.js'

/* First probe fires immediately; subsequent ones back off from here. */
const PROBE_BASE_MS = 1000
/* Capped so a long outage settles into a cheap once-every-30s heartbeat. */
const PROBE_MAX_MS = 30000

const probeDelay = (attempt: number) => Math.min(PROBE_BASE_MS * Math.pow(2, attempt - 1), PROBE_MAX_MS)

/*
 * Owns the "is the backend reachable" verdict for the whole app.
 *
 * The key property: **there is no polling while online.** Going offline is
 * reported by whatever actually failed (the websocket running out of attempts, a
 * failed request, the browser's offline event); only the recovery direction needs
 * probing, and that probe loop exists solely while status is 'offline'. A
 * heartbeat in the healthy case would be a request per tab forever — exactly the
 * churn this layer is meant to avoid.
 *
 * Consumers react to connectivityProbeSucceeded (see reconnectWebsocketEpic)
 * rather than reaching into the socket, so future subsystems that need to recover
 * after an outage can hook the same signal.
 */
export const connectivityEpic: Epic = (action$, state$, { ConnectivityRepository }) => {
  const isOffline = () => (source: Observable<unknown>) =>
    source.pipe(
      withLatestFrom(state$),
      filter(([, state]) => state.application.connectivity.status === 'offline')
    )

  /* One probe: announce it, then report the verdict. Never errors — a rejected
   * request IS the answer, not a failure of the loop. Deferred so every attempt
   * issues a fresh request instead of re-subscribing one captured at setup. */
  const probeOnce$: Observable<Action> = defer(() => ConnectivityRepository.checkHealth()).pipe(
    map((healthy) => (healthy ? connectivityProbeSucceeded() : connectivityProbeFailed())),
    catchError(() => of(connectivityProbeFailed())),
    startWith(connectivityProbeStarted())
  )

  /* Probe until something flips us back online, at which point the switchMap
   * below unsubscribes this whole chain. */
  const probeLoop$ = probeOnce$.pipe(repeat({ delay: (count) => timer(probeDelay(count)) }))

  return merge(
    /* Free signals from the browser. 'offline' is authoritative about the network
     * being gone; the other two are just cues to re-check, and only matter when we
     * already believe we are down (a focused tab on a healthy app must not probe). */
    fromEvent(window, 'offline').pipe(map(() => reportBackendUnreachable())),
    fromEvent(window, 'online').pipe(
      isOffline(),
      map(() => checkConnectivityNow())
    ),
    fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
      isOffline(),
      map(() => checkConnectivityNow())
    ),

    state$.pipe(
      map((state) => state.application.connectivity.status),
      distinctUntilChanged(),
      switchMap((status) =>
        status === 'online'
          ? EMPTY
          : /* A user-triggered check restarts the cadence from zero so "check now"
             * feels immediate instead of landing at the back of a 30s backoff. */
            merge(of(null), action$.pipe(filter(checkConnectivityNow.match))).pipe(switchMap(() => probeLoop$))
      )
    )
  )
}
